import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi, UserProfile, UpdateProfileData } from '../api/userApi';

/**
 * Query key for user profile
 */
export const userProfileQueryKey = ['user', 'profile'] as const;

/**
 * Hook for fetching current user's profile
 */
export const useUserProfile = () => {
  return useQuery<UserProfile, Error>({
    queryKey: userProfileQueryKey,
    queryFn: () => userApi.getProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook for updating user profile
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation<UserProfile, Error, UpdateProfileData>({
    mutationFn: (data) => userApi.updateProfile(data),
    onSuccess: (updatedProfile) => {
      // Update the profile in cache
      queryClient.setQueryData(userProfileQueryKey, updatedProfile);
    },
  });
};

/**
 * Hook that provides both profile data and update functionality
 */
export const useUserProfileWithUpdate = () => {
  const profileQuery = useUserProfile();
  const updateMutation = useUpdateProfile();

  return {
    profile: profileQuery.data ?? null,
    isLoading: profileQuery.isLoading,
    isUpdating: updateMutation.isPending,
    error: profileQuery.error?.message ?? updateMutation.error?.message ?? null,
    updateProfile: updateMutation.mutateAsync,
    refresh: profileQuery.refetch,
  };
};
